import { useEffect, useMemo, useRef, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ScanBarcode, Trash2, X, Gift } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { QRCodeCanvas } from "qrcode.react";
import { usePinPrompt } from "@/components/app/PinPrompt";

export default function POS() {
  const pinPrompt = usePinPrompt();
  const [items, setItems] = useState([]);
  const [parties, setParties] = useState([]);
  const [cart, setCart] = useState([]);
  const [scan, setScan] = useState(false);
  const [q, setQ] = useState("");
  const [firm, setFirm] = useState(null);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [partyId, setPartyId] = useState("");
  const [redeem, setRedeem] = useState(0);
  const scannerRef = useRef(null);

  useEffect(() => {
    (async () => {
      const [it, fm, ps] = await Promise.all([api.get("/items"), api.get("/firms/me"), api.get("/parties")]);
      setItems(it.data); setFirm(fm.data); setParties(ps.data);
    })();
  }, []);

  const selectedParty = useMemo(() => parties.find(p => p._id_str === partyId), [parties, partyId]);
  const loyaltyRules = firm?.loyalty || {};
  const availablePoints = selectedParty?.loyalty_points || 0;

  const filtered = useMemo(() =>
    !q ? items : items.filter((i) =>
      (i.name || "").toLowerCase().includes(q.toLowerCase()) ||
      (i.sku || "").toLowerCase().includes(q.toLowerCase()) ||
      (i.barcode || "").includes(q)
    ), [items, q]);

  const stockOf = (id) => { const it = items.find((x) => x._id_str === id); return !it || it.item_type === "service" ? Infinity : +it.stock || 0; };
  const addToCart = (it) => {
    const idx = cart.findIndex((c) => c.item_id === it._id_str);
    const inCart = idx >= 0 ? cart[idx].qty : 0;
    if (it.item_type !== "service" && inCart + 1 > (+it.stock || 0)) { toast.error(`No available stock of ${it.name}`); return; }
    if (idx >= 0) { const c = [...cart]; c[idx].qty += 1; setCart(c); }
    else setCart([...cart, { item_id: it._id_str, name: it.name, qty: 1, unit: it.unit, price: it.sale_price, discount: 0, gst_rate: it.gst_rate }]);
  };
  const setQty = (i, q) => {
    const c = [...cart];
    const max = stockOf(c[i].item_id);
    if (+q > max) toast.error(`No available stock — only ${max} of ${c[i].name}`);
    c[i].qty = Math.max(0, Math.min(+q, max));
    setCart(c);
  };
  const rm = (i) => setCart(cart.filter((_, x) => x !== i));

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    for (const c of cart) {
      const ls = c.qty * c.price - (c.discount || 0);
      sub += ls;
      tax += ls * (c.gst_rate || 0) / 100;
    }
    const redeemRate = +(loyaltyRules.redeem_rate || 0);
    const maxRedeemAmt = availablePoints * redeemRate;
    const redeemAmt = Math.min((+redeem || 0) * redeemRate, maxRedeemAmt, sub + tax);
    const total = +(sub + tax - redeemAmt).toFixed(2);
    const willEarn = +((total * (+(loyaltyRules.points_per_rupee) || 0))).toFixed(2);
    return { sub: +sub.toFixed(2), tax: +tax.toFixed(2), redeemAmt: +redeemAmt.toFixed(2), total, willEarn };
  }, [cart, redeem, loyaltyRules, availablePoints]);

  const startScan = async () => {
    setScan(true);
    setTimeout(async () => {
      try {
        const html5 = new Html5Qrcode("qr-reader");
        scannerRef.current = html5;
        await html5.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 } },
          async (code) => {
            try { const { data } = await api.get(`/items/lookup/${encodeURIComponent(code)}`); addToCart(data); toast.success(`Added ${data.name}`); }
            catch { toast.error(`No item for ${code}`); }
          }, () => {});
      } catch { toast.error("Camera access failed"); setScan(false); }
    }, 100);
  };
  const stopScan = async () => { try { await scannerRef.current?.stop(); scannerRef.current?.clear(); } catch {} setScan(false); };

  const checkout = async () => {
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    if ((+redeem || 0) > 0 && !partyId) { toast.error("Select party for loyalty redemption"); return; }
    await pinPrompt();
    try {
      const { data } = await api.post("/txns", {
        txn_type: "pos",
        party_id: partyId || null,
        party_name: selectedParty?.name || "Walk-in Customer",
        lines: cart, gst_enabled: cart.some(c => c.gst_rate > 0), intra_state: true,
        paid: totals.total, payment_mode: paymentMode,
        loyalty_redeemed: +redeem || 0,
      });
      const msg = `POS sale ${data.number}` + (data.loyalty_earned ? ` · earned ${data.loyalty_earned} pts` : "");
      toast.success(msg);
      setCart([]); setRedeem(0);
      // reload parties to refresh loyalty balance
      const ps = await api.get("/parties"); setParties(ps.data);
      window.open(`/print/txn/${data._id_str}`, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const upiLink = firm?.upi_id
    ? `upi://pay?pa=${firm.upi_id}&pn=${encodeURIComponent(firm.firm_name)}&am=${totals.total}&cu=INR`
    : null;

  return (
    <div>
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="overline">Quick Counter Sale</div>
          <h1 className="text-3xl font-bold tracking-tight">POS</h1>
        </div>
        <div className="flex gap-2 no-print">
          <Button variant="outline" onClick={startScan} data-testid="pos-scan-btn"><ScanBarcode size={14} /> Scan barcode</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-border rounded-lg p-4">
          <Input placeholder="Search products by name / SKU / barcode" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pos-search" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
            {filtered.map((it) => {
              const none = it.item_type !== "service" && (+it.stock || 0) <= 0;
              return (
              <button key={it._id_str} onClick={() => addToCart(it)} data-testid={`pos-item-${it._id_str}`}
                className={`text-left border rounded-md p-3 transition-colors ${none ? "border-red-200 bg-red-50/60 opacity-70 cursor-not-allowed" : "border-border hover:border-[#064E3B] hover:bg-emerald-50"}`}>
                <div className="font-semibold text-sm">{it.name}</div>
                <div className="text-xs text-slate-500 font-mono">{it.sku || "—"}</div>
                <div className="mt-2 flex items-center justify-between">
                  {none ? <span className="text-[11px] font-semibold text-red-700" data-testid={`pos-nostock-${it._id_str}`}>No available stock</span> : <span className="text-xs">{it.item_type === "service" ? "Service" : `Stock ${it.stock}`}</span>}
                  <span className="font-mono font-bold text-[#064E3B]">{fmtINR(it.sale_price)}</span>
                </div>
              </button>
              );
            })}
            {filtered.length === 0 && <div className="col-span-4 text-sm text-muted-foreground p-6 text-center">No items found</div>}
          </div>
        </div>

        <div className="bg-white border border-border rounded-lg p-4 space-y-3">
          <div>
            <Label className="overline">Customer</Label>
            <select value={partyId} onChange={(e) => setPartyId(e.target.value)} className="w-full border border-border rounded-md h-9 px-2 bg-white mt-1" data-testid="pos-party">
              <option value="">— Walk-in customer —</option>
              {parties.filter(p => p.kind !== "supplier").map((p) => <option key={p._id_str} value={p._id_str}>{p.name} · {p.loyalty_points || 0} pts</option>)}
            </select>
            {selectedParty && +(loyaltyRules.points_per_rupee || 0) > 0 && (
              <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 rounded p-2 flex items-center gap-2">
                <Gift size={14} className="text-emerald-700" />
                <span>Available: <b>{availablePoints}</b> pts · Will earn: <b>{totals.willEarn}</b> pts</span>
              </div>
            )}
          </div>

          <div className="overline">Cart</div>
          {cart.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">Cart is empty</div>}
          {cart.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-sm border-b border-border pb-2">
              <div className="flex-1">
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-slate-500 font-mono">{fmtINR(c.price)} {c.gst_rate ? `· ${c.gst_rate}% GST` : ""}</div>
              </div>
              <Input type="number" value={c.qty} onChange={(e) => setQty(i, e.target.value)} className="w-16 h-8 text-right" />
              <div className="w-20 text-right font-mono">{fmtINR(c.qty * c.price)}</div>
              <button className="text-red-600 hover:bg-red-50 p-1 rounded" onClick={() => rm(i)}><Trash2 size={14} /></button>
            </div>
          ))}

          <div className="border-t border-border pt-3 space-y-1 font-mono text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{fmtINR(totals.sub)}</span></div>
            {totals.tax > 0 && <div className="flex justify-between"><span>Tax</span><span>{fmtINR(totals.tax)}</span></div>}

            {selectedParty && availablePoints > 0 && +(loyaltyRules.redeem_rate || 0) > 0 && (
              <div className="flex items-center gap-2 pt-2">
                <Label className="text-xs">Redeem pts</Label>
                <Input type="number" min={0} max={availablePoints} value={redeem}
                  onChange={(e) => setRedeem(Math.min(+e.target.value, availablePoints))}
                  className="h-8 w-20 text-right" data-testid="pos-redeem" />
                <span className="text-xs text-emerald-700">− {fmtINR(totals.redeemAmt)}</span>
              </div>
            )}

            <div className="flex justify-between font-bold text-lg text-[#064E3B] border-t border-border pt-2 mt-2">
              <span>Total</span><span data-testid="pos-total">{fmtINR(totals.total)}</span>
            </div>

            <div className="mt-3 grid grid-cols-4 gap-2">
              {["cash", "upi", "bank", "cheque"].map((m) => (
                <button key={m} onClick={() => setPaymentMode(m)} data-testid={`pos-mode-${m}`}
                  className={`text-xs uppercase tracking-wider py-2 rounded border ${paymentMode === m ? "border-[#064E3B] bg-[#064E3B] text-white" : "border-border bg-white text-slate-700 hover:bg-slate-50"}`}>
                  {m}
                </button>
              ))}
            </div>

            {paymentMode === "upi" && (
              <div className="mt-3 text-center">
                {upiLink ? (
                  <div className="inline-block border border-border rounded p-3 bg-white" data-testid="pos-upi-qr">
                    <QRCodeCanvas value={upiLink} size={160} />
                    <div className="text-xs font-mono mt-2">{firm?.upi_id}</div>
                  </div>
                ) : <div className="text-xs text-amber-700">Set UPI ID in Settings to display QR.</div>}
              </div>
            )}

            <Button className="btn-primary w-full mt-3" onClick={checkout} data-testid="pos-checkout">Checkout & Print</Button>
          </div>
        </div>
      </div>

      {scan && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-4 max-w-md w-full">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Barcode Scanner</div>
              <button onClick={stopScan} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
            </div>
            <div id="qr-reader" className="rounded overflow-hidden" style={{ width: "100%" }}></div>
            <div className="text-xs text-muted-foreground mt-2">Point the camera at a product barcode.</div>
          </div>
        </div>
      )}
    </div>
  );
}
